import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DialogHeader, dialogActionsSx, dialogPaperProps } from "./DialogParts";
import { emailTrustCenterDocument } from "../../../apis/trust-center-apis/trustCenterApis";

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// An "Email only" document: it cannot be opened on the page, so the visitor
// types an email and the file is sent there right away. `resource` null:
// closed. In the admin's preview nothing is sent (`preview`).
const EmailDocumentDialog = ({
  resource,
  slug,
  companyName,
  preview = false,
  onClose,
  onSent,
}) => {
  const theme = useTheme();
  const open = !!resource;
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setBusy(false);
    setError("");
  }, [open]);

  const valid = EMAIL_PATTERN.test(email.trim());

  const send = async () => {
    if (preview) {
      onSent(
        "This is a preview, so nothing was sent. Visitors get the file at the email they enter."
      );
      onClose();
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await emailTrustCenterDocument({
        slug,
        email: email.trim(),
        resourceId: resource.id,
      });
      onSent(result?.message || `We emailed ${resource.name} to ${email}.`);
      onClose();
    } catch (err) {
      setError(err.message || "Could not send the document. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title={`Email me ${resource?.name || "this document"}`}
        onClose={busy ? undefined : onClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            {companyName || "The company"} shares this document by email only.
            Enter your work email and we will send it to you now.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ borderRadius: "8px" }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            onKeyDown={(e) => {
              if (e.key === "Enter" && valid && !busy) send();
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={send}
          disabled={busy || !valid}
          sx={{ textTransform: "none" }}
        >
          {busy ? "Sending…" : "Email me the document"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDocumentDialog;
